import { type Card, type Grade, Rating, createEmptyCard, fsrs } from "ts-fsrs";
import { z } from "zod";

/**
 * Spaced repetition with FSRS (spec §3: ts-fsrs, never a hand-rolled scheduler). State is per user per card;
 * the server computes every new state from the rating the client sends (§7.2 Cards / Review).
 */
export const RATINGS = ["again", "hard", "good", "easy"] as const;
export const ratingSchema = z.enum(RATINGS);
export type ReviewRating = z.infer<typeof ratingSchema>;

const GRADE: Record<ReviewRating, Grade> = { again: Rating.Again, hard: Rating.Hard, good: Rating.Good, easy: Rating.Easy };

/** Stored card state (ISO dates), mirroring ts-fsrs's Card. */
export const cardStateSchema = z.object({
  due: z.string(),
  stability: z.number(),
  difficulty: z.number(),
  elapsed_days: z.number(),
  scheduled_days: z.number(),
  learning_steps: z.number().int(),
  reps: z.number().int(),
  lapses: z.number().int(),
  state: z.number().int().min(0).max(3),
  last_review: z.string().nullable(),
});
export type CardState = z.infer<typeof cardStateSchema>;

// Fuzz off: the same history always gives the same schedule, so offline replays agree with the server.
const scheduler = fsrs({ enable_fuzz: false });

const toCard = (s: CardState): Card => ({
  ...s,
  due: new Date(s.due),
  last_review: s.last_review ? new Date(s.last_review) : undefined,
});
const fromCard = (c: Card): CardState => ({
  due: c.due.toISOString(),
  stability: c.stability,
  difficulty: c.difficulty,
  elapsed_days: c.elapsed_days,
  scheduled_days: c.scheduled_days,
  learning_steps: c.learning_steps,
  reps: c.reps,
  lapses: c.lapses,
  state: c.state,
  last_review: c.last_review ? c.last_review.toISOString() : null,
});

export function newCardState(at: Date): CardState {
  return fromCard(createEmptyCard(at));
}

export function gradeCard(current: CardState | null, rating: ReviewRating, at: Date): CardState {
  const card = current ? toCard(current) : createEmptyCard(at);
  return fromCard(scheduler.next(card, at, GRADE[rating]).card);
}

/** "<1m", "6m", "3h", "4d", "2mo": how long until a card returns, for the label on a rating button. */
export function intervalLabel(ms: number): string {
  const minutes = ms / 60_000;
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = hours / 24;
  if (days < 30) return `${Math.round(days)}d`;
  return days < 365 ? `${Math.round(days / 30)}mo` : `${Math.round((days / 365) * 10) / 10}y`;
}

/** When each rating would bring the card back, so "Again / Hard / Good / Easy" show their next interval (§5.4 #8). */
export function ratingIntervals(current: CardState | null, at: Date): Record<ReviewRating, string> {
  const out = {} as Record<ReviewRating, string>;
  for (const r of RATINGS) out[r] = intervalLabel(Date.parse(gradeCard(current, r, at).due) - at.getTime());
  return out;
}

/** Probability the student still recalls the card now (0–1); new cards count as 0 (§10.5 readiness). */
export function retrievability(state: CardState | null, now: Date): number {
  if (!state || state.reps === 0) return 0;
  return scheduler.get_retrievability(toCard(state), now, false);
}

export const gradeRequestSchema = z.object({
  rating: ratingSchema,
  /** When the student graded it — offline grades sync later and keep their real time (§11.1). */
  reviewed_at: z.string().datetime(),
  /** Unique per grade on the device: a re-sent grade is recorded once, never double-counted. */
  client_review_id: z.string().min(8).max(80),
});
export type GradeRequest = z.infer<typeof gradeRequestSchema>;

export const reviewCardSchema = z.object({
  id: z.string().uuid(),
  set_id: z.string().uuid(),
  front: z.string(),
  back: z.string(),
  source_chunk_ids: z.array(z.string().uuid()),
  status: z.enum(["active", "flagged"]),
  user_authored: z.boolean(),
  state: cardStateSchema.nullable(),
});
export type ReviewCard = z.infer<typeof reviewCardSchema>;

export const reviewCardListSchema = z.object({ items: z.array(reviewCardSchema), due_count: z.number().int().nonnegative() });
export const gradeResponseSchema = z.object({ card_id: z.string().uuid(), state: cardStateSchema, duplicate: z.boolean() });
